import React, { useEffect, useState } from 'react';
import { BarChart } from 'lucide-react';
import Toast from '../components/Toast';

const VOUCHER_API_URL = 'https://api.epsonrewards.my/v1/epson/checkVoucherBalance';
const VOUCHER_API_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoxMjMsImV4cCI6NDg4NzIyODgyNn0.vWUOB7q8p28_b1Yl0sRTPYRoefEZ5iOXhgjDHAhESlI';



interface VoucherBalanceItem {
  id: number;
  category: string;
  voucher_quantity: number;
  redeemed_count: number;
  remaining_count: number;
  valid_purchased_date_start: string;
}

interface VoucherBalanceResponse {
  status: number;
  message: string;
  data: VoucherBalanceItem[];
}

export default function CampaignBalance() {
  const [voucherData, setVoucherData] = useState<VoucherBalanceItem[]>([]);
  const [allVoucherData, setAllVoucherData] = useState<VoucherBalanceItem[]>([]);
  const [dateOptions, setDateOptions] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState({show: false, message: ''});
  const [toastId, setToastId] = useState(0);
  const [campaignName, setCampaignName] = useState('Epson Campaign');
  const [sortBy, setSortBy] = useState<'category' | 'percentage'>('percentage');





  const fetchVoucherData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(VOUCHER_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${VOUCHER_API_TOKEN}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch voucher data: ${response.status} ${response.statusText}`);
      }
      
      const result: VoucherBalanceResponse = await response.json();
      
      if (result.status !== 200) {
        throw new Error(`API returned error: ${result.message}`);
      }

      // Extract unique valid_purchased_date_start values
      const uniqueDates = Array.from(new Set(result.data.map(item => item.valid_purchased_date_start)))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      setDateOptions(uniqueDates);

      setAllVoucherData(result.data); // Store all data

      // Filter by selected date if not 'all'
      let filteredData = result.data;
      if (selectedDate !== 'all') {
        filteredData = result.data.filter(item => item.valid_purchased_date_start === selectedDate);
      }
      
      // Create a copy of the data for sorting
      // let sortedData = [...result.data];
      
      // Sort based on current sort preference
      if (sortBy === 'category') {
        filteredData.sort((a, b) => a.category.localeCompare(b.category));
      } else {
        // Sort by percentage of vouchers used (highest percentage first)
        filteredData.sort((a, b) => {
          const percentA = a.redeemed_count / a.voucher_quantity;
          const percentB = b.redeemed_count / b.voucher_quantity;
          return percentB - percentA;
        });
      }
      
      setVoucherData(filteredData);
      

      setToast({
        show: true,
        message: 'Voucher balance data refreshed successfully'
      });
      setToastId(prev => prev + 1);
      setTimeout(() => {
        setToast({ show: false, message: '' });
      }, 3000);
    } catch (err) {
      console.error('Error fetching voucher balance data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (allVoucherData.length === 0) {
      fetchVoucherData();
    } else {
      let filteredData = allVoucherData;
      if (selectedDate !== 'all') {
        filteredData = allVoucherData.filter(item => item.valid_purchased_date_start === selectedDate);
      }
      if (sortBy === 'category') {
        filteredData = [...filteredData].sort((a, b) => a.category.localeCompare(b.category));
      } else {
        filteredData = [...filteredData].sort((a, b) => {
          const percentA = a.redeemed_count / a.voucher_quantity;
          const percentB = b.redeemed_count / b.voucher_quantity;
          return percentB - percentA;
        });
      }
      setVoucherData(filteredData);
    }
  }, [selectedDate, sortBy]);

  useEffect(() => {
    fetchVoucherData();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Campaign Voucher Balance</h1>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <label htmlFor="dateFilter" className="mr-2 text-sm text-gray-600 dark:text-gray-300">Filter by Date:</label>
              <select
                id="dateFilter"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="all">All Dates</option>
                {dateOptions.map(date => (
                  <option key={date} value={date}>{date}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <label htmlFor="sortBy" className="mr-2 text-sm text-gray-600 dark:text-gray-300">Sort by:</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'category' | 'percentage')}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="percentage">Usage Percentage</option>
                <option value="category">Category Name</option>
              </select>
            </div>
            {isLoading && (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded-md">
            <p>{error}</p>
          </div>
        )}

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">{campaignName}</h2>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 transition-colors duration-200">
            <table className="min-w-full bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800 transition-colors duration-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                    Vouchers Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                    Voucher Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                    Progress
                  </th>
                  {sortBy === 'percentage' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                      Usage %
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={sortBy === 'percentage' ? 5 : 4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 transition-colors duration-200">
                      Loading...
                    </td>
                  </tr>
                ) : voucherData.length > 0 ? (
                  voucherData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200">
                        {item.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200">
                        {item.redeemed_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200">
                        {`${item.redeemed_count}/${item.voucher_quantity} (${item.remaining_count} remaining)`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              (item.redeemed_count / item.voucher_quantity) > 0.9 
                                ? 'bg-red-500' 
                                : (item.redeemed_count / item.voucher_quantity) > 0.7 
                                  ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, (item.redeemed_count / item.voucher_quantity) * 100)}%` }}
                          ></div>
                        </div>
                      </td>
                      {sortBy === 'percentage' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200">
                          {((item.redeemed_count / item.voucher_quantity) * 100).toFixed(1)}%
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={sortBy === 'percentage' ? 5 : 4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 transition-colors duration-200">
                      No campaign data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6">
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
              <span>Running Low</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
              <span>Almost Depleted</span>
            </div>
          </div>
        </div>
      </div>
      
      {toast.show && (
        <Toast 
          message={toast.message} 
          onClose={() => setToast({show: false, message: ''})}
          id={toastId}
        />
      )}
    </div>
  );
}
